import toast from 'react-hot-toast';

/**
 * Standard error structure from API responses
 */
export interface ApiError {
    status?: number;
    data?: {
        detail?: string;
        message?: string;
        errors?: Record<string, string[]>;
    } | string;
    message?: string;
}

/**
 * Options for customizing error handling behavior
 */
export interface ErrorHandlerOptions {
    /**
     * Custom message to display for 500 errors
     */
    serverErrorMessage?: string;

    /**
     * Custom handler for specific error conditions
     * Return true to stop default error handling
     */
    customHandler?: (error: ApiError) => boolean;

    /**
     * Whether to show toast notifications (default: true)
     */
    showToast?: boolean;

    /**
     * Prefix to add to error messages
     */
    messagePrefix?: string;
}

/**
 * Checks if the error response contains HTML content (typical for 500 errors)
 */
const isHtmlResponse = (data: unknown): boolean => {
    if (typeof data !== 'string') return false;

    return data.includes('<!DOCTYPE') || data.includes('<html');
};

const GENERIC_ERROR_MESSAGE = 'There was an issue processing this request. Please try again later or contact our Support Team for assistance.'
/**
 * Extracts a user-friendly error message from an API error
 */
export const getErrorMessage = (error: unknown): string => {
    const _error = error as ApiError;

    // Handle 500 errors or HTML responses
    if (_error?.status === 500 || isHtmlResponse(_error?.data)) {
        return GENERIC_ERROR_MESSAGE;
    }

    // Handle string data (non-JSON responses)
    if (typeof _error?.data === 'string') {
        return 'There was an issue processing this request. Please try again later or contact our Support Team for assistance.';
    }

    // Extract error detail or message
    const errorDetail = _error?.data?.detail || '';
    const errorMessage = _error?.data?.message || '';

    // Handle validation errors
    if (_error?.data?.errors) {
        const errorMessages = Object.values(_error.data.errors).flat().join(', ');
        return `Validation error: ${errorMessages}`;
    }

    // Return the first available error message or a fallback
    return errorDetail || errorMessage || _error?.message || 'An unexpected error occurred';
};

/**
 * Handles API errors with toast notifications and optional custom handling
 * 
 * @param error - The error object from the API
 * @param options - Options for customizing error handling behavior
 * @returns The error message that was displayed
 * 
 * @example
 * ```tsx
 * try {
 *   await createContact(data).unwrap();
 *   toast.success('Contact created successfully!');
 * } catch (error) {
 *   handleApiError(error, {
 *     messagePrefix: 'Failed to create contact.',
 *     customHandler: (err) => {
 *       if (err?.data?.detail?.includes('Contact exists')) {
 *         toast.error('Contact with this ID already exists.');
 *         return true; // Stop default error handling
 *       }
 *       return false; // Continue with default error handling
 *     }
 *   });
 * }
 * ```
 */
export const handleApiError = (
    error: unknown,
    options: ErrorHandlerOptions = {}
): string => {
    const {
        serverErrorMessage,
        customHandler,
        showToast = true,
        messagePrefix
    } = options;

    const _error = error as ApiError;

    // Run custom handler first if provided
    if (customHandler?.(_error)) {
        return ''; // Custom handler handled the error
    }

    // Handle 500 errors or HTML responses
    if (_error?.status === 500 || isHtmlResponse(_error?.data)) {
        const message = serverErrorMessage || GENERIC_ERROR_MESSAGE;

        if (showToast) {
            toast.error(message);
        }

        return message;
    }

    // Handle string data (non-JSON responses)
    if (typeof _error?.data === 'string') {
        const message = serverErrorMessage || GENERIC_ERROR_MESSAGE;

        if (showToast) {
            toast.error(message);
        }

        return message;
    }

    // Get the error message
    const baseMessage = getErrorMessage(error);
    const fullMessage = messagePrefix ? `${messagePrefix} ${baseMessage}` : baseMessage;

    if (showToast) {
        toast.error(fullMessage);
    }

    return fullMessage;
};

/**
 * Checks if an error is a specific type of error by examining the error detail
 * 
 * @example
 * ```tsx
 * if (isErrorType(error, 'Contact exists')) {
 *   // Handle duplicate contact error
 * }
 * ```
 */
export const isErrorType = (error: unknown, searchString: string): boolean => {
    const _error = error as ApiError;

    if (typeof _error?.data === 'string') return false;

    const errorDetail = _error?.data?.detail || '';
    const errorMessage = _error?.data?.message || '';

    return (
        (typeof errorDetail === 'string' && errorDetail.toLowerCase().includes(searchString.toLowerCase())) ||
        (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes(searchString.toLowerCase()))
    );
};

/**
 * Checks if an error is a server error (5xx status code)
 */
export const isServerError = (error: unknown): boolean => {
    const _error = error as ApiError;
    return _error?.status !== undefined && _error.status >= 500 && _error.status < 600;
};

/**
 * Checks if an error is a client error (4xx status code)
 */
export const isClientError = (error: unknown): boolean => {
    const _error = error as ApiError;
    return _error?.status !== undefined && _error.status >= 400 && _error.status < 500;
};
